import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import API from '@/lib/api';
import { orderedMenu, serializeMenu } from '@/lib/menuOrder';

export function useMenuOrder(user, defaults) {
  const context = user?._id ? `${user._id}:${user.verkoper_id || 'owner'}` : '';
  const [state, setState] = useState({ context: '', groups: [], revision: 0, status: 'loading', error: '' });
  const [attempt, setAttempt] = useState(0);
  const live = useRef(null);
  const locked = useRef(false);
  const failed = useRef(null);
  useEffect(() => {
    const current = { context };
    live.current = current;
    locked.current = false;
    failed.current = null;
    setState({ context, groups: [], revision: 0, status: 'loading', error: '' });
    if (!context) return;
    API.get('/voorkeuren/menu', { noCache: true, timeout: 10000 }).then(({ data }) => {
      if (live.current === current) setState({ ...data, context, status: 'ready', error: '' });
    }).catch(() => {
      if (live.current === current) setState(s => ({ ...s, status: 'load-error', error: 'Menuvolgorde niet geladen.' }));
    });
    return () => { if (live.current === current) live.current = null; };
  }, [context, attempt]);

  const save = useCallback(async groups => {
    if (locked.current || state.context !== context || !['ready', 'saved', 'save-error'].includes(state.status)) return;
    locked.current = true;
    const current = live.current;
    const previous = state.groups;
    setState(s => ({ ...s, groups, status: 'saving', error: '' }));
    try {
      const { data } = await API.put('/voorkeuren/menu', { context, revision: state.revision, groups }, { timeout: 10000 });
      if (live.current !== current) return;
      failed.current = null;
      setState({ ...data, context, status: 'saved', error: '' });
    } catch (e) {
      if (live.current !== current) return;
      const conflict = e.response?.status === 409;
      failed.current = conflict ? null : groups;
      setState(s => ({ ...s, groups: previous, status: conflict ? 'load-error' : 'save-error',
        error: conflict ? 'Menu elders gewijzigd. Laad opnieuw.' : 'Niet bewaard. Vorige volgorde hersteld.' }));
    } finally { if (live.current === current) locked.current = false; }
  }, [context, state]);

  const current = state.context === context ? state : { groups: [], status: 'loading', error: '' };
  const groups = useMemo(() => orderedMenu(defaults, current.groups), [defaults, current.groups]);
  return { groups, status: current.status, error: current.error,
    canEdit: ['ready', 'saved', 'save-error'].includes(current.status),
    change: next => save(serializeMenu(next)), reset: () => save([]),
    retry: () => failed.current ? save(failed.current) : setAttempt(v => v + 1) };
}